import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { MagnifyingGlass, Plus, MapPin, ArrowLeft, X, List, MapTrifold, Star } from "@phosphor-icons/react";
import CoursesMap from "../components/CoursesMap";
import ConfidenceBadge from "../components/ConfidenceBadge";
import { useAuth } from "../contexts/AuthContext";

export default function Courses() {
  const [courses, setCourses] = useState([]);
  const [q, setQ] = useState("");
  // Country filter: "" = all, otherwise a country slug like "uk", "spain", "portugal".
  const [country, setCountry] = useState(() => localStorage.getItem("fairwayCountryFilter") || "");
  const [showAdd, setShowAdd] = useState(false);
  const [view, setView] = useState("list"); // list | map
  const [prefilledCoords, setPrefilledCoords] = useState(null);
  const [focusCoords, setFocusCoords] = useState(null);
  const { user, toggleFavourite } = useAuth();
  const favIds = user?.favourite_course_ids || [];

  const load = async () => {
    // Stale-while-revalidate: render the cached list immediately, then fetch
    // fresh data in the background so the user never sees a blank screen.
    const { data } = await api.get("/courses/lite");
    setCourses(data);
    try {
      localStorage.setItem("fairwayCoursesCacheV2", JSON.stringify({ at: Date.now(), data }));
    } catch {}
  };
  useEffect(() => {
    // 1) Paint cached data instantly if we have it (≤ 24h old)
    try {
      const raw = localStorage.getItem("fairwayCoursesCacheV2");
      if (raw) {
        const { at, data } = JSON.parse(raw);
        if (data && Array.isArray(data) && Date.now() - (at || 0) < 24 * 60 * 60 * 1000) {
          setCourses(data);
        }
      }
    } catch {}
    // 2) Always refresh in the background so the list stays current
    load();
  }, []);

  const handleCourseAdded = (newCourse) => {
    setShowAdd(false);
    setPrefilledCoords(null);
    load();
    // Compute centroid of new course and pan map to it
    const coords = (newCourse?.holes || []).filter((h) => h.lat != null && h.lng != null);
    if (coords.length) {
      const lat = coords.reduce((s, h) => s + h.lat, 0) / coords.length;
      const lng = coords.reduce((s, h) => s + h.lng, 0) / coords.length;
      setView("map");
      // bump the object so React detects change even if coords identical
      setFocusCoords({ lat, lng, zoom: 14, _at: Date.now() });
    }
  };

  // Detect the country a course belongs to by sniffing the `location`
  // string. We fall back to "uk" because every legacy OSM import was UK-only,
  // so UK courses have a "United Kingdom" / "England" / "Scotland" / "Wales"
  // / "Northern Ireland" / "" location. The Iberia importer tags Spain &
  // Portugal explicitly.
  const countryOf = (c) => {
    const loc = (c?.location || "").toLowerCase();
    if (loc.includes("spain")) return "spain";
    if (loc.includes("portugal")) return "portugal";
    return "uk";
  };

  // Counts per country so the pills can show "🇬🇧 UK · 2,900".
  const counts = courses.reduce(
    (acc, c) => {
      const k = countryOf(c);
      acc[k] = (acc[k] || 0) + 1;
      acc.all += 1;
      return acc;
    },
    { all: 0, uk: 0, spain: 0, portugal: 0 },
  );

  const filtered = courses.filter((c) => {
    if (country && countryOf(c) !== country) return false;
    const ql = q.toLowerCase();
    return c.name.toLowerCase().includes(ql) || c.location.toLowerCase().includes(ql);
  });

  // Sort favourites to the top when there's no search query, so it doubles as a quick-access shelf.
  const sorted = q
    ? filtered
    : [...filtered].sort((a, b) => {
        const aFav = favIds.includes(a.id);
        const bFav = favIds.includes(b.id);
        if (aFav === bFav) return 0;
        return aFav ? -1 : 1;
      });

  const pickCountry = (next) => {
    setCountry(next);
    try { localStorage.setItem("fairwayCountryFilter", next); } catch {}
    // Auto-zoom the map to the picked country's centroid. The /courses/lite
    // payload exposes a precomputed `centroid_lat/lng` per course (the full
    // `holes[]` array isn't shipped) so we average those.
    const targets = next
      ? courses.filter((c) => countryOf(c) === next)
      : courses;
    const coords = targets
      .map((c) => (c.centroid_lat != null && c.centroid_lng != null) ? { lat: c.centroid_lat, lng: c.centroid_lng } : null)
      .filter(Boolean);
    if (coords.length === 0) return;
    const lat = coords.reduce((s, h) => s + h.lat, 0) / coords.length;
    const lng = coords.reduce((s, h) => s + h.lng, 0) / coords.length;
    setFocusCoords({ lat, lng, zoom: next ? 6 : 5, _at: Date.now() });
  };

  return (
    <div className="px-5 pt-6">
      <div className="flex items-center justify-between mb-5">
        <Link to="/app" className="btn-ghost -ml-2" data-testid="courses-back-link">
          <ArrowLeft size={20} weight="bold" />
        </Link>
        <button
          onClick={() => setShowAdd(true)}
          data-testid="add-course-button"
          className="btn-ghost"
        >
          <Plus size={18} weight="bold" /> Add course
        </button>
      </div>

      <h1 className="font-heading font-black tracking-tighter-2 text-4xl text-[var(--primary)] mb-1">Golf courses</h1>
      <p className="text-[var(--text-secondary)] mb-4">
        {country
          ? `${counts[country] ?? 0} in ${country === "uk" ? "the UK" : country.charAt(0).toUpperCase() + country.slice(1)} · Tap one to start a round`
          : `${courses.length} courses · Tap one to start a round`}
      </p>

      {/* View toggle */}
      <div className="grid grid-cols-2 gap-2 mb-4 p-1 bg-[var(--secondary)] rounded-full">
        <button
          onClick={() => setView("list")}
          data-testid="view-toggle-list"
          className={`rounded-full py-2 text-sm font-heading font-bold flex items-center justify-center gap-2 transition-colors ${
            view === "list" ? "bg-white text-[var(--primary)] shadow" : "text-[var(--primary)]/70"
          }`}
        >
          <List size={16} weight="bold" /> List
        </button>
        <button
          onClick={() => setView("map")}
          data-testid="view-toggle-map"
          className={`rounded-full py-2 text-sm font-heading font-bold flex items-center justify-center gap-2 transition-colors ${
            view === "map" ? "bg-white text-[var(--primary)] shadow" : "text-[var(--primary)]/70"
          }`}
        >
          <MapTrifold size={16} weight="bold" /> Map
        </button>
      </div>

      {/* Country filter pills — visible in both list AND map views */}
      <div
        className="flex gap-1.5 overflow-x-auto pb-2 mb-3 -mx-1 px-1 scrollbar-hide"
        data-testid="country-filter-pills"
      >
        {[
          { id: "",         flag: "🌍", label: "All",      n: counts.all },
          { id: "uk",       flag: "🇬🇧", label: "UK",       n: counts.uk },
          { id: "spain",    flag: "🇪🇸", label: "Spain",    n: counts.spain },
          { id: "portugal", flag: "🇵🇹", label: "Portugal", n: counts.portugal },
        ].map((opt) => {
          const active = country === opt.id;
          if (opt.id && opt.n === 0) return null; // hide pills with zero courses
          return (
            <button
              key={opt.id || "all"}
              type="button"
              onClick={() => pickCountry(opt.id)}
              data-testid={`country-pill-${opt.id || "all"}`}
              className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-heading font-bold transition-all whitespace-nowrap ${
                active
                  ? "bg-[var(--primary)] text-white shadow"
                  : "bg-[var(--secondary)] text-[var(--primary)] hover:bg-[var(--secondary)]/80"
              }`}
            >
              <span aria-hidden>{opt.flag}</span>
              {opt.label}
              <span
                className={`text-[10px] font-mono-num font-normal opacity-80 ${active ? "" : "text-[var(--text-secondary)]"}`}
              >
                {opt.n}
              </span>
            </button>
          );
        })}
      </div>

      {view === "map" ? (
        <CoursesMap
          courses={filtered}
          focusCoords={focusCoords}
          onPickToAdd={(coords) => {
            setPrefilledCoords(coords);
            setShowAdd(true);
          }}
        />
      ) : (
        <>
          <div className="relative mb-3">
            <MagnifyingGlass size={18} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] pointer-events-none" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search course or location"
              className="input-field"
              style={{ paddingLeft: "2.75rem", paddingRight: "3rem" }}
              data-testid="courses-search-input"
            />
          </div>

          <div className="space-y-3" data-testid="courses-list">
            {sorted.map((c) => {
              const isFav = favIds.includes(c.id);
              return (
                <div
                  key={c.id}
                  data-testid={`course-row-${c.id}`}
                  className="surface-card-flat overflow-hidden hover:shadow-md transition-shadow relative"
                >
                  <Link to={`/app/courses/${c.id}`} className="block">
                    <div className="flex">
                      <div className="h-24 w-28 flex-shrink-0 bg-[var(--secondary)] overflow-hidden">
                        {c.image_url && <img src={c.image_url} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div className="flex-1 p-3 pr-12 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="font-heading font-bold text-[var(--primary)] text-base line-clamp-1 flex-1 min-w-0">{c.name}</div>
                          {c.confidence && <ConfidenceBadge confidence={c.confidence} />}
                        </div>
                        <div className="text-xs text-[var(--text-secondary)] flex items-center gap-1 mt-1">
                          <MapPin size={12} weight="fill" /> <span className="line-clamp-1">{c.location}</span>
                        </div>
                        <div className="flex gap-4 mt-2 text-xs">
                          {c.stats_unknown ? (
                            <span className="italic text-[var(--text-secondary)]">Stats not imported yet</span>
                          ) : (
                            <>
                              <span><span className="label-uppercase">Par</span> <span className="font-mono-num font-bold">{c.par_total}</span></span>
                              <span><span className="label-uppercase">Yds</span> <span className="font-mono-num font-bold">{c.yards_total}</span></span>
                              <span><span className="label-uppercase">Holes</span> <span className="font-mono-num font-bold">{c.hole_count ?? c.holes?.length ?? 0}</span></span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavourite(c.id); }}
                    data-testid={`favourite-toggle-${c.id}`}
                    aria-label={isFav ? "Remove from favourites" : "Add to favourites"}
                    className="absolute top-2 right-2 h-9 w-9 rounded-full flex items-center justify-center transition-colors"
                    style={{
                      background: isFav ? "var(--accent-terracotta)" : "rgba(20,61,47,0.06)",
                      color: isFav ? "#F2EEE7" : "var(--primary)",
                    }}
                  >
                    <Star size={18} weight={isFav ? "fill" : "regular"} />
                  </button>
                </div>
              );
            })}
            {!q && sorted.length === 0 && (
              <div className="text-center text-sm text-[var(--text-secondary)] py-12">No courses yet — add one above.</div>
            )}
            {q && sorted.length === 0 && (
              <div className="text-center text-sm text-[var(--text-secondary)] py-12">No matches for &ldquo;{q}&rdquo;.</div>
            )}
          </div>
        </>
      )}

      {showAdd && <AddCourseDialog
        prefilledCoords={prefilledCoords}
        onClose={() => { setShowAdd(false); setPrefilledCoords(null); }}
        onAdded={handleCourseAdded}
      />}
    </div>
  );
}

function AddCourseDialog({ onClose, onAdded, prefilledCoords }) {
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [numHoles, setNumHoles] = useState(18);
  const initialHoles = Array.from({ length: 18 }, (_, i) => ({
    number: i + 1,
    par: 4,
    yards: 380,
    lat: i === 0 && prefilledCoords ? prefilledCoords.lat.toFixed(6) : "",
    lng: i === 0 && prefilledCoords ? prefilledCoords.lng.toFixed(6) : "",
  }));
  const [holes, setHoles] = useState(initialHoles);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const setHole = (i, key, v) => {
    const next = [...holes];
    next[i] = { ...next[i], [key]: v };
    setHoles(next);
  };

  const changeHoleCount = (n) => {
    setNumHoles(n);
    setHoles(Array.from({length:n}, (_,i) => holes[i] || ({ number: i+1, par: 4, yards: 380, lat: "", lng: "" })));
  };

  const useMyLocation = (i) => {
    if (!navigator.geolocation) { setError("Geolocation not supported"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => setHoles((prev) => prev.map((h, idx) => idx===i ? { ...h, lat: pos.coords.latitude.toFixed(6), lng: pos.coords.longitude.toFixed(6) } : h)),
      (err) => setError(err.message),
      { enableHighAccuracy: true }
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        location: location.trim(),
        holes: holes.slice(0, numHoles).map((h) => ({
          number: Number(h.number),
          par: Number(h.par),
          yards: Number(h.yards),
          lat: h.lat ? Number(h.lat) : null,
          lng: h.lng ? Number(h.lng) : null,
        })),
      };
      const { data } = await api.post("/courses", payload);
      onAdded(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" data-testid="add-course-dialog">
      <div className="bg-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-[var(--border-default)] px-5 py-4 flex items-center justify-between">
          <h2 className="font-heading font-bold text-xl text-[var(--primary)]">Add course</h2>
          <button onClick={onClose} className="btn-ghost" data-testid="close-add-course"><X size={20} weight="bold" /></button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          {prefilledCoords && (
            <div className="rounded-2xl bg-[var(--accent-sand)] border border-[var(--border-default)] p-3 text-xs text-[var(--primary)]" data-testid="map-prefill-banner">
              <div className="font-heading font-bold">Hole 1 location dropped from map</div>
              <div className="font-mono-num mt-1">
                {prefilledCoords.lat.toFixed(5)}, {prefilledCoords.lng.toFixed(5)}
              </div>
              <div className="mt-1 text-[var(--text-secondary)]">Fill in name, par/yardage, and other hole locations.</div>
            </div>
          )}
          <div>
            <label className="label-uppercase block mb-2">Course name</label>
            <input className="input-field" value={name} onChange={(e)=>setName(e.target.value)} required data-testid="new-course-name" />
          </div>
          <div>
            <label className="label-uppercase block mb-2">Location</label>
            <input className="input-field" value={location} onChange={(e)=>setLocation(e.target.value)} required placeholder="e.g. Surrey, England" data-testid="new-course-location" />
          </div>
          <div>
            <label className="label-uppercase block mb-2">Holes</label>
            <div className="flex gap-2">
              {[9,18].map((n) => (
                <button type="button" key={n} onClick={()=>changeHoleCount(n)}
                  data-testid={`hole-count-${n}`}
                  className={`flex-1 py-3 rounded-full font-heading font-bold ${numHoles===n ? "bg-[var(--primary)] text-white" : "bg-[var(--secondary)] text-[var(--primary)]"}`}>
                  {n} holes
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <label className="label-uppercase block">Hole details</label>
            {holes.slice(0, numHoles).map((h, i) => (
              <div key={`hole-${h.number}`} className="rounded-xl border border-[var(--border-default)] p-3 bg-[var(--bg-app)]">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-heading font-bold text-[var(--primary)]">Hole {h.number}</div>
                  <button type="button" onClick={()=>captureMyLocation(i)} className="text-xs underline text-[var(--primary)]" data-testid={`use-gps-hole-${h.number}`}>Use my GPS</button>
                </div>
                <div className="grid grid-cols-4 gap-2 text-sm">
                  <Field label="Par" value={h.par} onChange={(v)=>setHole(i,"par",v)} testid={`hole-${h.number}-par`} />
                  <Field label="Yards" value={h.yards} onChange={(v)=>setHole(i,"yards",v)} testid={`hole-${h.number}-yards`} />
                  <Field label="Lat" value={h.lat} onChange={(v)=>setHole(i,"lat",v)} testid={`hole-${h.number}-lat`} placeholder="opt." />
                  <Field label="Lng" value={h.lng} onChange={(v)=>setHole(i,"lng",v)} testid={`hole-${h.number}-lng`} placeholder="opt." />
                </div>
              </div>
            ))}
          </div>

          {error && <div className="text-sm text-[var(--status-bad)] bg-red-50 border border-red-200 rounded-xl px-4 py-3">{error}</div>}

          <button type="submit" disabled={submitting} className="btn-primary w-full" data-testid="submit-new-course">
            {submitting ? "Saving..." : "Save course"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, placeholder }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)] font-semibold">{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-white border border-[var(--border-default)] rounded-lg px-2 py-2 text-sm font-mono-num focus:border-[var(--primary)] outline-none"
      />
    </label>
  );
}
